import React, {useCallback, useRef} from 'react';
import {createPortal} from 'react-dom';

import {
    DndContext,
    DropAnimation,
    MouseSensor,
    TouchSensor,
    Modifiers,
    UniqueIdentifier,
    useSensors,
    useSensor,
    MeasuringStrategy,
    defaultDropAnimationSideEffects
} from '@dnd-kit/core';
import {SortableContext, verticalListSortingStrategy, SortingStrategy} from '@dnd-kit/sortable';

import {Container, DroppableContainer} from './parts';
import {DragOverlay} from './parts/DragOverlay';
import {SortableItem} from './parts/Item';
import {EmptyContent} from './parts/Container/EmptyContent';
import {Group} from '../../store/HomepageStore';
import {useHomepageState} from '../../hooks/useHomepageState';
import {DefaultGroupsIDs} from '../../enums';
import {showTilesSearch} from '../../actions/dialogs';
import {useScrollToActiveSection} from '../../hooks/useScrollToActiveSection';
import {useUpdateGridGaps} from '../../hooks/useUpdateGridGaps';
import {HomepageItemData} from '../../api';

const dropAnimation: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
        styles: {
            active: {
                opacity: '0.5'
            }
        }
    })
};

interface Props {
    itemsMap?: Map<UniqueIdentifier, UniqueIdentifier[]>;
    strategy?: SortingStrategy;
    modifiers?: Modifiers;
    groupsData: Map<string, Group>;
    items: {[id: string]: HomepageItemData};
    onDragEnd?: (itemsMap?: Map<UniqueIdentifier, UniqueIdentifier[]>) => void;
    portletsKey: string;
}

const commonSensorOptions = {
    activationConstraint: {distance: 10}
};

export function HomepageGroups(props: Props) {
    const {
        items,
        groupsData,
        modifiers,
        itemsMap: initialItemsMap,
        strategy = verticalListSortingStrategy,
        onDragEnd: onItemsChange,
        portletsKey
    } = props;

    const {state, handlers} = useHomepageState({
        initialItemsMap,
        onChange: onItemsChange
    });

    const {itemsMap, activeId} = state;

    const {collisionDetectionStrategy, onDragOver, onDragEnd, onDragCancel, onDragStart} = handlers;

    const containers = [...itemsMap.keys()];
    const isSortingContainer = activeId ? containers.includes(activeId as string) : false;
    const gridRef = useRef<HTMLDivElement>();

    const getGridRef = useCallback(() => gridRef.current, []);

    const sensors = useSensors(
        useSensor(MouseSensor, commonSensorOptions),
        useSensor(TouchSensor, commonSensorOptions)
    );

    useScrollToActiveSection();

    useUpdateGridGaps(gridRef);

    const renderDragOverlay = () => {
        const containerData = groupsData.get(activeId as string);
        return (
            <DragOverlay dropAnimation={dropAnimation} getGridRef={getGridRef} activeId={activeId}>
                {activeId && containerData ? (
                    <Container
                        {...containerData}
                        id="dragging-container-placeholder"
                        isDragging
                        count={containerData?.items?.length || 0}
                    />
                ) : null}
            </DragOverlay>
        );
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={collisionDetectionStrategy}
            measuring={{droppable: {strategy: MeasuringStrategy.Always}}}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
            onDragCancel={onDragCancel}
            modifiers={modifiers}
        >
            <div
                style={{display: 'flex', flexDirection: 'column', gap: 24, paddingBottom: 24}}
                ref={gridRef}
            >
                <SortableContext items={containers} strategy={verticalListSortingStrategy}>
                    {containers.map((containerId: UniqueIdentifier) => {
                        const data = groupsData.get(containerId as string);
                        const childrenMap = itemsMap.get(containerId as string);
                        // In case we removed container from the store, but it's still in the itemsMap until next re-render
                        if (!data) return null;

                        const isWidget = containerId === DefaultGroupsIDs.insights;
                        return (
                            <DroppableContainer
                                key={containerId}
                                id={containerId as string}
                                items={childrenMap}
                                type={data.type}
                                title={data.title}
                                isHidden={data.isHidden}
                            >
                                <SortableContext items={childrenMap} strategy={strategy}>
                                    {childrenMap && childrenMap.length > 0 ? (
                                        childrenMap.map((value, index) => {
                                            const itemData = items[value];

                                            return (
                                                <SortableItem
                                                    key={
                                                        isWidget ? `${portletsKey}-${value}` : value
                                                    }
                                                    handle={false}
                                                    disabled={isSortingContainer}
                                                    id={value}
                                                    containerId={containerId}
                                                    itemType={data?.type}
                                                    data={itemData}
                                                />
                                            );
                                        })
                                    ) : (
                                        <EmptyContent
                                            containerType={data.type}
                                            isShortcuts={data.title === DefaultGroupsIDs.shortcuts}
                                            addNewItemToGroup={() => showTilesSearch(data.title)}
                                        />
                                    )}
                                </SortableContext>
                            </DroppableContainer>
                        );
                    })}
                </SortableContext>
            </div>

            {createPortal(renderDragOverlay(), document.body)}
        </DndContext>
    );
}
